import { useForm, FormProvider, Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Fade, Typography } from '@mui/material';
import { useRememberDevice } from '../../hooks/useRememberDevice';
import { LoginFormType, LoginFormValues } from './types';
import useNotifications from '../../../_lib/notifications/useNotifications';
import NextPublicWrapper from '../NextPublicWrapper';
import IntakeForm from './forms/IntakeForm';
import EmailForm from './forms/EmailForm';
import SsoForm from './forms/SsoForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import React from 'react';
import { InputLabelBase } from '../../inputs/InputLabelBase';
import { EmailField } from './forms/helpers';
import { Show } from '../../Show';
import _ from 'lodash';
import { consumeOAuthRedirect } from './oauthRedirect.utils';
import {
  getPostLoginDestination,
  type LoginLocationState,
} from './postLoginDestination';

const NewLogin = () => {
  const history = useHistory();
  const location = useLocation<LoginLocationState>();
  const notifications = useNotifications();
  const isSSOLoginEnabled = useFeatureIsOn(FEATURE_FLAG_KEYS.SSO_LOGIN);
  const rememberDevice = useRememberDevice({
    onSuccess: () => {
      const oauthRedirect = consumeOAuthRedirect();
      if (oauthRedirect) {
        window.location.replace(oauthRedirect);
        return;
      }
      history.replace(getPostLoginDestination(location.state));
    },
    onFailure: () => {
      setTimeout(() => {
        notifications.info(
          'Your current session has expired. Please log back into the platform to refresh your session.'
        );
      }, 1000);
    },
  });

  const methods = useForm<LoginFormValues>({
    defaultValues: {
      username: '',
      password: '',
      rememberDevice: rememberDevice.isRemembered,
      mfaEnabled: false,
      sso: {
        enabled: false,
        loginFormType: LoginFormType.INTAKE,
        isLoading: false,
      },
    },
    resolver: yupResolver(
      loginFormSchema
    ) as unknown as Resolver<LoginFormValues>,
  });

  React.useEffect(() => {
    const oauthRedirect = new URLSearchParams(window.location.search).get(
      'oauth_redirect'
    );
    if (oauthRedirect) {
      sessionStorage.setItem('mcp_oauth_redirect', oauthRedirect);
    }
  }, []);

  const loginFormType = methods.watch('sso.loginFormType');
  const dirtyFields = methods.formState.dirtyFields;
  React.useEffect(() => {
    if (!isSSOLoginEnabled || loginFormType === LoginFormType.INTAKE) {
      return;
    }

    if (dirtyFields.username) {
      methods.setValue('sso.loginFormType', LoginFormType.INTAKE);
      methods.setValue('password', '');
    }
  }, [dirtyFields.username, isSSOLoginEnabled, loginFormType, methods]);

  React.useEffect(() => {
    const errorToken = window.localStorage.getItem('tokenGenerationFailure');
    if (errorToken) {
      window.localStorage.removeItem('tokenGenerationFailure');
      notifications.error(
        _.isString(errorToken)
          ? errorToken
          : 'An error occurred while generating the token. Please try again.'
      );
    }
  }, [notifications]);

  if (rememberDevice.isRemembered && rememberDevice.isLoading) {
    return null;
  }

  const _isEmail = loginFormType === LoginFormType.EMAIL;
  const _isSSO = loginFormType === LoginFormType.SSO;
  const _isIntake = loginFormType === LoginFormType.INTAKE;

  return (
    <NextPublicWrapper>
      <Box my={1}>
        <Typography className="tertia-prime-text">
          Nice to see you again,
        </Typography>
        <Typography className="canon-text">Welcome back.</Typography>
      </Box>

      <FormProvider {...methods}>
        <InputLabelBase
          style={{
            color: '#fff',
            paddingTop: 0,
          }}
        >
          Email{' '}
        </InputLabelBase>

        <EmailField autoFocus />

        <Fade key={loginFormType} in timeout={800}>
          <Box minHeight={250}>
            <Show when={isSSOLoginEnabled} fallback={<EmailForm />}>
              <Show when={_isIntake}>
                <IntakeForm />
              </Show>
              <Show when={_isEmail}>
                <EmailForm />
              </Show>
              <Show when={_isSSO}>
                <SsoForm />
              </Show>
            </Show>
          </Box>
        </Fade>
      </FormProvider>
    </NextPublicWrapper>
  );
};

export default NewLogin;
const loginFormSchema = yup.object().shape({
  username: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});
